import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, X, Type, AlignLeft, CheckSquare, List, Calendar, Loader2 } from 'lucide-react';
import {
  FormFieldDefinition,
  FormFieldType,
  FormType,
  FormTemplate,
  createFormTemplate,
  updateFormTemplate,
  getIntakeFieldsPreview,
} from '../../utils/formsApi';

interface FormBuilderProps {
  template?: FormTemplate | null;
  onSave: () => void;
  onCancel: () => void;
}

const FIELD_TYPES: { type: FormFieldType; label: string; icon: React.ReactNode }[] = [
  { type: 'text', label: 'Short Text', icon: <Type className="w-4 h-4" /> },
  { type: 'textarea', label: 'Long Text', icon: <AlignLeft className="w-4 h-4" /> },
  { type: 'checkbox', label: 'Checkbox', icon: <CheckSquare className="w-4 h-4" /> },
  { type: 'select', label: 'Dropdown', icon: <List className="w-4 h-4" /> },
  { type: 'date', label: 'Date', icon: <Calendar className="w-4 h-4" /> },
];

const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const FormBuilder: React.FC<FormBuilderProps> = ({ template, onSave, onCancel }) => {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [formType, setFormType] = useState<FormType>(template?.form_type || 'general');
  const [fields, setFields] = useState<FormFieldDefinition[]>(template?.fields || []);
  const [intakeMapping, setIntakeMapping] = useState<Record<string, string>>(
    template?.intake_mapping || {}
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingIntakeFields, setLoadingIntakeFields] = useState(false);

  // Auto-populate fields when switching to intake type (for new forms only)
  const handleFormTypeChange = async (newType: FormType) => {
    setFormType(newType);
    setError(null);
    
    // Only auto-populate for new forms (not editing existing)
    if (newType === 'intake' && !template && fields.length === 0) {
      setLoadingIntakeFields(true);
      try {
        const { fields: intakeFields, intake_mapping } = await getIntakeFieldsPreview();
        if (intakeFields.length > 0) {
          setFields(intakeFields);
          setIntakeMapping(intake_mapping);
          setName('Patient Intake Form');
          setDescription('Pre-visit intake questionnaire for pet owners');
        }
      } catch (err) {
        console.error('Failed to load intake fields:', err);
        // Don't show error - user can still add fields manually
      } finally {
        setLoadingIntakeFields(false);
      }
    }
  };

  // Load intake fields on mount if creating new intake form
  useEffect(() => {
    if (formType === 'intake' && !template && fields.length === 0) {
      handleFormTypeChange('intake');
    }
  }, []);

  const addField = (type: FormFieldType) => {
    const newField: FormFieldDefinition = {
      id: generateFieldId(),
      type,
      label: `New ${FIELD_TYPES.find(f => f.type === type)?.label || 'Field'}`,
      required: false,
      options: type === 'select' ? ['Option 1', 'Option 2'] : undefined,
    };
    setFields([...fields, newField]);
  };

  const updateField = (index: number, updates: Partial<FormFieldDefinition>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const removeField = (index: number) => {
    const newFields = fields.filter((_, i) => i !== index);
    setFields(newFields);
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= fields.length) return;
    const newFields = [...fields];
    const [movedField] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, movedField);
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Form name is required');
      return;
    }

    if (fields.length === 0) {
      setError('Add at least one field to the form');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data = {
        name: name.trim(),
        description: description.trim() || undefined,
        form_type: formType,
        fields,
        intake_mapping: formType === 'intake' ? intakeMapping : undefined,
        is_active: true,
      };

      if (template) {
        await updateFormTemplate(template.id, data);
      } else {
        await createFormTemplate(data);
      }

      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {template ? 'Edit Form' : 'Create New Form'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Patient Intake Form"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Form Type</label>
            <select
              value={formType}
              onChange={(e) => handleFormTypeChange(e.target.value as FormType)}
              disabled={loadingIntakeFields}
              className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="general">General Form</option>
              <option value="intake">Intake Form (links to encounters)</option>
            </select>
            {loadingIntakeFields && (
              <p className="text-sm text-blue-600 mt-1 flex items-center">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Loading intake questions...
              </p>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description of this form..."
          />
        </div>
      </div>

      {/* Field Types Toolbar */}
      <div className="border-b border-gray-200 p-4 bg-gray-50">
        <p className="text-sm font-medium text-gray-700 mb-2">Add Field:</p>
        <div className="flex flex-wrap gap-2">
          {FIELD_TYPES.map((fieldType) => (
            <button
              key={fieldType.type}
              onClick={() => addField(fieldType.type)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
            >
              {fieldType.icon}
              <span className="ml-1.5">{fieldType.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Fields List */}
      <div className="p-6">
        {fields.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <AlignLeft className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium text-gray-900 mb-2">No fields yet</p>
            <p className="text-sm">Click on a field type above to add it to your form</p>
          </div>
        ) : (
          <div className="space-y-4">
            {fields.map((field, index) => (
              <FieldEditor
                key={field.id}
                field={field}
                index={index}
                totalFields={fields.length}
                onUpdate={(updates) => updateField(index, updates)}
                onRemove={() => removeField(index)}
                onMoveUp={() => moveField(index, index - 1)}
                onMoveDown={() => moveField(index, index + 1)}
                isIntakeForm={formType === 'intake'}
                intakeMapping={intakeMapping}
                onIntakeMappingChange={setIntakeMapping}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? 'Saving...' : 'Save Form'}
        </button>
      </div>
    </div>
  );
};

interface FieldEditorProps {
  field: FormFieldDefinition;
  index: number;
  totalFields: number;
  onUpdate: (updates: Partial<FormFieldDefinition>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isIntakeForm: boolean;
  intakeMapping: Record<string, string>;
  onIntakeMappingChange: (mapping: Record<string, string>) => void;
}

const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  index,
  totalFields,
  onUpdate,
  onRemove,
  onMoveUp,
  onMoveDown,
  isIntakeForm,
  intakeMapping,
  onIntakeMappingChange,
}) => {
  const fieldTypeInfo = FIELD_TYPES.find((f) => f.type === field.type);

  const getIntakeMappingKey = () => {
    return Object.entries(intakeMapping).find(([_, fieldId]) => fieldId === field.id)?.[0] || '';
  };

  const handleIntakeMappingChange = (intakeKey: string) => {
    const newMapping = { ...intakeMapping };
    // Remove old mapping for this field
    Object.keys(newMapping).forEach((key) => {
      if (newMapping[key] === field.id) delete newMapping[key];
    });
    // Add new mapping if selected
    if (intakeKey) {
      newMapping[intakeKey] = field.id;
    }
    onIntakeMappingChange(newMapping);
  };

  return (
    <div className="border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        {/* Drag Handle */}
        <div className="flex flex-col gap-1 pt-2">
          <button
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            title="Move up"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </div>

        {/* Field Content */}
        <div className="flex-1 space-y-3">
          {/* Field Type Badge */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
              {fieldTypeInfo?.icon}
              <span className="ml-1">{fieldTypeInfo?.label}</span>
            </span>
          </div>

          {/* Label */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Label</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Options for select fields */}
          {field.type === 'select' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-2">
                Options
              </label>
              <div className="space-y-2">
                {(field.options || []).map((option, optIndex) => (
                  <div key={optIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...(field.options || [])];
                        newOptions[optIndex] = e.target.value;
                        onUpdate({ options: newOptions });
                      }}
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      placeholder={`Option ${optIndex + 1}`}
                    />
                    <button
                      onClick={() => {
                        const newOptions = (field.options || []).filter((_, i) => i !== optIndex);
                        onUpdate({ options: newOptions.length > 0 ? newOptions : ['Option 1'] });
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Remove option"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newOptions = [...(field.options || []), ''];
                    onUpdate({ options: newOptions });
                  }}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Option
                </button>
              </div>
            </div>
          )}

          {/* Placeholder */}
          {(field.type === 'text' || field.type === 'textarea') && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Placeholder (optional)
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => onUpdate({ placeholder: e.target.value || undefined })}
                className="w-full px-3 py-2 text-sm border border-gray-300 focus:ring-2 focus:ring-blue-500"
                placeholder="Enter placeholder text..."
              />
            </div>
          )}

          {/* Required Toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate({ required: e.target.checked })}
              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Required field</span>
          </label>

          {/* Intake Mapping (for intake forms) */}
          {isIntakeForm && (field.type === 'text' || field.type === 'textarea') && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Map to Encounter Intake Field
              </label>
              <select
                value={getIntakeMappingKey()}
                onChange={(e) => handleIntakeMappingChange(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Not mapped --</option>
                <option value="chief_complaint">Chief Complaint</option>
                <option value="history">History</option>
                <option value="notes">Notes</option>
              </select>
            </div>
          )}
        </div>

        {/* Delete Button */}
        <button
          onClick={onRemove}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title="Remove field"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default FormBuilder;


